import React from "react";
import { computeSegmentDuration, formatDurationMinutes } from "@/utils/segmentDuration";

/**
 * Day-of travel breakdown for the Dashboard's Next Milestone card.
 *
 * When the next major milestone is a transit segment, the user often has
 * MORE travel happening that same day — connecting flights, rideshares to/
 * from a station, multiple train legs, etc. This card surfaces a single
 * total + a per-leg breakdown so they know how long the whole day's travel
 * is going to take.
 *
 * Pure-render component; the parent passes the `same_day_transit` array
 * exactly as returned by `/api/dashboard/next-milestone`.
 */
const TYPE_ICON = {
  flight: "✈️",
  train: "🚆",
  bus: "🚌",
  cruise: "🚢",
  rideshare: "🚗",
};
const TYPE_NOUN = {
  flight: "flight",
  train: "train",
  bus: "bus",
  cruise: "cruise",
  rideshare: "rideshare",
};

const SameDayTransitSummary = ({ segments }) => {
  if (!Array.isArray(segments) || segments.length === 0) return null;

  // Compute each leg's duration. Skip segments that don't yield a number
  // (missing time, cancellation already filtered upstream).
  const legs = segments
    .map((s) => {
      const d = computeSegmentDuration(s);
      if (!d || !(d.minutes > 0)) return null;
      return {
        id: s.id,
        type: s.type,
        minutes: d.minutes,
        formatted: d.formatted,
        approx: !d.tzAware && s.type === "flight",
      };
    })
    .filter(Boolean);

  if (legs.length === 0) return null;

  const totalMin = legs.reduce((acc, l) => acc + l.minutes, 0);
  const totalFmt = formatDurationMinutes(totalMin);
  if (!totalFmt) return null;

  // Hide when there's only one leg AND no extra value vs the existing
  // SegmentDurationBadge — the badge already says "✈️ 2h 15m". One-leg days
  // here would just repeat that. Multi-leg days are the win.
  if (legs.length < 2) return null;

  const anyApprox = legs.some((l) => l.approx);

  return (
    <div
      data-testid="same-day-transit-summary"
      className="mt-3 rounded-lg border border-sky-200 dark:border-sky-800 bg-white/60 dark:bg-slate-900/30 px-3 py-2"
    >
      <div className="text-[11px] uppercase tracking-wider font-medium text-sky-700 dark:text-sky-300">
        Travel planned on this day
      </div>
      <div
        className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100"
        data-testid="same-day-transit-total"
      >
        {anyApprox ? "~" : ""}{totalFmt} total
      </div>
      <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 leading-snug break-words">
        {legs.map((leg, idx) => (
          <span
            key={leg.id || idx}
            data-testid={`same-day-transit-leg-${idx}`}
            className="inline-block"
          >
            <span className="mr-1">{TYPE_ICON[leg.type] || "•"}</span>
            <span className="whitespace-nowrap">
              {leg.approx ? "~" : ""}{leg.formatted} {TYPE_NOUN[leg.type] || leg.type}
            </span>
            {idx < legs.length - 1 && (
              <span className="mx-1.5 text-gray-400 dark:text-slate-500">+</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

export default SameDayTransitSummary;
